import { useCallback, useEffect, useRef, useState } from "react";
import { getModelFromCache, saveModelToCache } from "../cache/modelCache";
import { applyMask } from "../canvas/maskApply";

export type InferenceState =
	| { status: "idle" }
	| { status: "loading-model"; progress: number }
	| { status: "ready" }
	| { status: "processing" }
	| { status: "error"; message: string };

const MODEL_URL =
	"https://pub-98508814100146828bb9115c9d1f0342.r2.dev/isnet-general-use.onnx";
const MODEL_CACHE_KEY = "isnet-v1";

export function useInference() {
	const workerRef = useRef<Worker | null>(null);
	const [state, setState] = useState<InferenceState>({ status: "idle" });
	const pendingRef = useRef<
		Map<
			string,
			{
				resolve: (result: {
					foreground: HTMLCanvasElement;
					mask: ImageData;
				}) => void;
				reject: (err: Error) => void;
				imageData: ImageData;
				canvas: HTMLCanvasElement;
			}
		>
	>(new Map());

	useEffect(() => {
		// Create worker
		const worker = new Worker(
			new URL("../../workers/inference.worker.ts", import.meta.url),
			{
				type: "module",
			},
		);

		worker.onmessage = (event) => {
			const msg = event.data;

			if (msg.type === "loaded") {
				setState({ status: "ready" });
			} else if (msg.type === "progress") {
				setState({
					status: "loading-model",
					progress: (msg.loaded / msg.total) * 100,
				});
			} else if (msg.type === "result") {
				const pending = pendingRef.current.get(msg.id);
				if (pending) {
					pendingRef.current.delete(msg.id);
					const { imageData, canvas } = pending;
					const resultCanvas = applyMask(
						imageData,
						msg.mask,
						msg.width,
						msg.height,
						canvas,
					);
					const maskImageData = new ImageData(
						new Uint8ClampedArray(msg.mask.length * 4),
						Math.sqrt(msg.mask.length),
						Math.sqrt(msg.mask.length),
					);
					pending.resolve({ foreground: resultCanvas, mask: maskImageData });
					setState({ status: "ready" });
				}
			} else if (msg.type === "error") {
				const pendingEntries = Array.from(pendingRef.current.entries());
				pendingRef.current.clear();
				for (const [, p] of pendingEntries) {
					p.reject(new Error(msg.message));
				}
				setState({ status: "error", message: msg.message });
			}
		};

		worker.onerror = (err) => {
			setState({ status: "error", message: err.message });
		};

		workerRef.current = worker;

		// Load model (from IndexedDB cache or network)
		async function loadModel() {
			setState({ status: "loading-model", progress: 0 });

			try {
				// Try IndexedDB cache first
				const cached = await getModelFromCache(MODEL_CACHE_KEY);
				if (cached) {
					const url = URL.createObjectURL(new Blob([cached]));
					worker.postMessage({ type: "load", modelUrl: url });
					return;
				}

				// Fetch from network with progress tracking
				const response = await fetch(MODEL_URL);
				if (!response.ok)
					throw new Error(`Failed to fetch model: ${response.status}`);

				const contentLength = response.headers.get("content-length");
				const total = contentLength ? parseInt(contentLength, 10) : 0;
				const reader = response.body?.getReader();
				if (!reader) throw new Error("Response body is not readable");
				const chunks: Uint8Array[] = [];
				let loaded = 0;

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;
					chunks.push(value);
					loaded += value.length;
					if (total > 0) {
						setState({
							status: "loading-model",
							progress: (loaded / total) * 100,
						});
					}
				}

				const buffer = mergeChunks(chunks, loaded);

				// Cache for next time
				await saveModelToCache(MODEL_CACHE_KEY, buffer).catch(() => {
					// Quota exceeded — silently ignore
				});

				const blob = new Blob([buffer]);
				const url = URL.createObjectURL(blob);
				worker.postMessage({ type: "load", modelUrl: url });
			} catch (err) {
				setState({
					status: "error",
					message: err instanceof Error ? err.message : String(err),
				});
			}
		}

		loadModel();

		return () => {
			worker.terminate();
		};
	}, []);

	const removeBackground = useCallback(
		(
			imageFile: File,
		): Promise<{ foreground: HTMLCanvasElement; mask: ImageData }> => {
			return new Promise((resolve, reject) => {
				if (!workerRef.current || state.status !== "ready") {
					reject(new Error("Model not ready"));
					return;
				}

				const img = new Image();
				const objectUrl = URL.createObjectURL(imageFile);
				img.onload = () => {
					URL.revokeObjectURL(objectUrl);
					const canvas = document.createElement("canvas");
					canvas.width = img.width;
					canvas.height = img.height;
					const ctx = canvas.getContext("2d")!;
					ctx.drawImage(img, 0, 0);
					const imageData = ctx.getImageData(0, 0, img.width, img.height);

					const id = crypto.randomUUID();
					pendingRef.current.set(id, { resolve, reject, imageData, canvas });
					setState({ status: "processing" });
					workerRef.current?.postMessage({ type: "infer", imageData, id }, [
						imageData.data.buffer,
					]);
				};
				img.onerror = () => {
					URL.revokeObjectURL(objectUrl);
					reject(new Error("Failed to load image"));
				};
				img.src = objectUrl;
			});
		},
		[state.status],
	);

	return { state, removeBackground };
}

function mergeChunks(chunks: Uint8Array[], totalSize: number): ArrayBuffer {
	const merged = new Uint8Array(totalSize);
	let offset = 0;
	for (const chunk of chunks) {
		merged.set(chunk, offset);
		offset += chunk.length;
	}
	return merged.buffer;
}
